import type { RouteLocationGeneric, RouteRecordRaw } from "vue-router";

// Creates a bilingual (en/ar) redirect pair sharing the same canonical path builder.
// Params are cast to string — Vue Router always produces scalar strings for named params
// defined without array-modifier patterns (e.g., `:id+`, `:id*`).
function langRedirects(
  enPath: string,
  arPath: string,
  canonical: (to: RouteLocationGeneric) => string,
): RouteRecordRaw[] {
  return [
    { path: enPath, redirect: canonical },
    { path: arPath, redirect: canonical },
  ];
}

// Auth routes — inside the auth shell (requiresAuth inherited from parent)
export const reportsAuthRoutes: RouteRecordRaw[] = [
  // Operations
  {
    path: "/review",
    name: "projects-review",
    component: () => import("@/views/Operations/ProjectsReview.vue"),
  },
  // Audit Report
  {
    path: "/audit-reports",
    name: "audit-reports",
    component: () => import("@/views/AuditReport/AuditReports.vue"),
  },
  // Audit Report (unified bilingual)
  {
    path: "/audit-reports/new",
    name: "audit-reports-new",
    component: () => import("@/views/AuditReport/AuditReport.vue"),
  },
  {
    path: "/audit-reports/edit/:audit_report_id",
    name: "audit-reports-edit",
    props: true,
    component: () => import("@/views/AuditReport/AuditReport.vue"),
  },
  // Backward-compat redirects for old language-specific routes
  { path: "/audit-reports/new/en", redirect: "/audit-reports/new" },
  { path: "/audit-reports/new/ar", redirect: "/audit-reports/new" },
  ...langRedirects(
    "/audit-reports/edit/en/:audit_report_id",
    "/audit-reports/edit/ar/:audit_report_id",
    (to) => `/audit-reports/edit/${to.params.audit_report_id as string}`,
  ),
  // Qualified Report
  {
    path: "/qualified-reports",
    name: "qualified-reports",
    component: () => import("@/views/QualifiedReport/QualifiedReports.vue"),
  },
  // Qualified Report (unified bilingual)
  {
    path: "/qualified-reports/new",
    name: "qualified-reports-new",
    component: () => import("@/views/QualifiedReport/QualifiedReport.vue"),
  },
  {
    path: "/qualified-report/:qualified_report_id?",
    name: "qualified-report",
    props: true,
    component: () => import("@/views/QualifiedReport/QualifiedReport.vue"),
  },
  // Backward-compat redirects
  { path: "/qualified-reports/new/en", redirect: "/qualified-reports/new" },
  { path: "/qualified-reports/new/ar", redirect: "/qualified-reports/new" },
  ...langRedirects(
    "/qualified-report-en/:qualified_report_id?",
    "/qualified-report-ar/:qualified_report_id?",
    (to) =>
      (to.params.qualified_report_id as string)
        ? `/qualified-report/${to.params.qualified_report_id as string}`
        : "/qualified-reports/new",
  ),

  // SSB Report (unified bilingual)
  {
    path: "/ssb-reports/edit/:ssb_report_id",
    name: "ssb-reports-edit",
    props: true,
    component: () => import("@/views/SSBReport/SSBReport.vue"),
  },
  // Backward-compat redirects
  ...langRedirects(
    "/ssb-reports/edit/en/:ssb_report_id",
    "/ssb-reports/edit/ar/:ssb_report_id",
    (to) => `/ssb-reports/edit/${to.params.ssb_report_id as string}`,
  ),

  // Excel Export and MM Reports
  {
    path: "/excel-export",
    name: "ExcelExport",
    component: () => import("@/views/Reports/ExcelExport.vue"),
  },
  {
    path: "/mm-report",
    name: "MMReports",
    component: () => import("@/views/Reports/MMReports.vue"),
  },
  // Financial Obligations Review — flag-gated (financial_obligation_reviewer),
  // backend also enforces via 403; menu entry hidden client-side for non-reviewers.
  {
    path: "/financial-obligations-review",
    name: "financial-obligations-review",
    component: () =>
      import("@/views/Reports/FinancialObligationsReview/FinancialObligationsReview.vue"),
  },
  // Scholar Usage reports — management view over every SAM's records.
  // `meta.moduleKey` bounces a user without the grant to the dashboard rather
  // than rendering a shell that 403s on every call; the backend gate on
  // /api/scholar-engagement/reports is the real boundary.
  {
    path: "/scholar-engagement-reports",
    name: "scholar-engagement-reports",
    meta: { moduleKey: "scholar_engagement_reports" },
    component: () =>
      import("@/views/Reports/ScholarEngagementReports/ScholarEngagementReports.vue"),
  },
  // Per-SAM drill-down. Same grant as the roll-up it is reached from -- a
  // `meta.moduleKey` present on one of the two routes and missing from the
  // other is exactly the gap no unit test catches.
  // The `(\d+)` constraint means a non-numeric id never mounts the view. The
  // backend rejects it too, with a 404: `web::Path<i32>`'s own `PathError`
  // answers before the handler runs (confirmed empirically, and pinned by
  // `a_non_numeric_sam_id_is_rejected_before_the_handler`).
  {
    path: "/scholar-engagement-reports/sams/:samId(\\d+)",
    name: "scholar-engagement-report-sam",
    meta: { moduleKey: "scholar_engagement_reports" },
    component: () =>
      import("@/views/Reports/ScholarEngagementReports/SamDetailReport.vue"),
  },
];

// Public routes — no auth required
export const reportsPublicRoutes: RouteRecordRaw[] = [
  // Response Collection — intentionally public, external audited parties fill this form
  {
    path: "/collect-response/:audit_report_id/:audit_report_name",
    name: "collect-response",
    props: true,
    component: () =>
      import("@/views/AuditReport/ResponseCollection/ResponseForm.vue"),
  },
  // Backward-compat redirects
  ...langRedirects(
    "/collect-response-en/:audit_report_id/:audit_report_name",
    "/collect-response-ar/:audit_report_id/:audit_report_name",
    (to) =>
      `/collect-response/${to.params.audit_report_id as string}/${to.params.audit_report_name as string}`,
  ),
];
